import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Briefcase, Plane, Puzzle, FileText, Users } from "lucide-react";
import { API } from "@/lib/api";

const MODULE_ICONS = { crm: Briefcase, travel: Plane, workforce: Users };

export const ModulesCard = ({ subscription }) => {
  const [modules, setModules] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState({});
  const [selectedPlan, setSelectedPlan] = useState("starter");
  const [busy, setBusy] = useState("");
  useEffect(() => {
    axios.get(`${API}/billing/modules`).then((r) => setModules(r.data || [])).catch(() => {});
    axios.get(`${API}/billing/plans`).then((r) => setPlans(r.data || [])).catch(() => {});
  }, []);
  const booked = (subscription && subscription.modules) || {};
  const isOwner = subscription && (subscription.owner || subscription.demo);
  const basePlan = isOwner ? "business" : subscription?.plan;
  const book = async (moduleId) => {
    const tier = selected[moduleId] || "basic";
    setBusy(moduleId);
    try {
      const r = await axios.post(`${API}/billing/module/checkout`, {
        module: moduleId, tier, interval: "month", origin_url: window.location.origin,
      });
      if (r.data.url) window.open(r.data.url, "_blank", "noopener,noreferrer"); else toast.error("Kein Bezahllink verfügbar");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout konnte nicht gestartet werden");
      setBusy("");
    }
  };
  const bookBasePlan = async () => {
    setBusy("__base");
    try {
      const r = await axios.post(`${API}/billing/checkout`, {
        plan: selectedPlan, interval: "month", origin_url: window.location.origin,
      });
      if (r.data.url) window.open(r.data.url, "_blank", "noopener,noreferrer"); else toast.error("Kein Bezahllink verfügbar");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout konnte nicht gestartet werden");
      setBusy("");
    }
  };
  if (modules.length === 0) return null;
  return (
    <Card className="p-6 bg-white border-[var(--brand-border)] lg:col-span-2" data-testid="settings-modules-card">
      <div className="flex items-start gap-2 mb-1">
        <Puzzle className="w-5 h-5 text-[var(--brand-primary)] mt-1" />
        <div>
          <h2 className="font-[Playfair_Display] text-xl font-semibold text-[var(--brand-ink)]">Module &amp; Add-ons</h2>
          <p className="text-sm text-[var(--brand-muted)]">Erweitere dein Studio um zusätzliche Module – jederzeit dazubuchbar, jederzeit kündbar.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div className="rounded-xl border border-[var(--brand-border)] p-4" data-testid="module-card-billing">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2 font-semibold text-[var(--brand-ink)]"><FileText className="w-4 h-4 text-[var(--brand-primary)]" /> Business Studio Suite</div>
            {basePlan ? (
              <span className="text-xs px-2.5 py-1 rounded-full bg-green-100 text-green-700 font-semibold capitalize" data-testid="module-status-billing">✓ {basePlan}</span>
            ) : (
              <span className="text-xs px-2.5 py-1 rounded-full bg-[var(--brand-border-light)] text-[var(--brand-muted)]" data-testid="module-status-billing">Nicht gebucht</span>
            )}
          </div>
          {!basePlan && plans.length > 0 && (
            <div className="flex items-center gap-2 mt-3">
              <select
                value={selectedPlan}
                onChange={(e) => setSelectedPlan(e.target.value)}
                className="h-9 rounded-md border border-[var(--brand-border)] bg-white px-2 text-sm flex-1"
                data-testid="module-tier-select-billing"
              >
                {plans.map((p) => {
                  const label = `${p.name} – ${p.month.toLocaleString("de-DE")} €/Monat`;
                  return (<option key={p.id} value={p.id}>{label}</option>);
                })}
              </select>
              <Button size="sm" onClick={bookBasePlan} disabled={busy === "__base"}
                className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="module-book-billing">
                {busy === "__base" ? "…" : "Buchen"}
              </Button>
            </div>
          )}
          <p className="text-xs text-[var(--brand-muted)] mt-2">
            {basePlan ? "Angebote, Rechnungen & Online-Zahlung sind freigeschaltet." : "Basistarif für Angebote, Rechnungen & Online-Zahlung."}
          </p>
        </div>
        {modules.map((m) => {
          const Icon = MODULE_ICONS[m.id] || Puzzle;
          const b = booked[m.id];
          const isActive = isOwner || (b && b.status === "active");
          const activeTier = isOwner ? "premium" : b?.tier;
          const activeTierName = activeTier ? (m.tiers.find((t) => t.id === activeTier)?.name || activeTier) : null;
          const sel = selected[m.id] || "basic";
          const selTier = m.tiers.find((t) => t.id === sel) || m.tiers[0];
          return (
            <div key={m.id} className="rounded-xl border border-[var(--brand-border)] p-4" data-testid={`module-card-${m.id}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2 font-semibold text-[var(--brand-ink)]"><Icon className="w-4 h-4 text-[var(--brand-primary)]" /> {m.name}</div>
                {isActive ? (
                  <span className="text-xs px-2.5 py-1 rounded-full bg-green-100 text-green-700 font-semibold capitalize" data-testid={`module-status-${m.id}`}>✓ {activeTierName || "aktiv"}</span>
                ) : (
                  <span className="text-xs px-2.5 py-1 rounded-full bg-[var(--brand-border-light)] text-[var(--brand-muted)]" data-testid={`module-status-${m.id}`}>Nicht gebucht</span>
                )}
              </div>
              {!isActive && (
                <>
                  <div className="flex items-center gap-2 mt-3">
                    <select
                      value={sel}
                      onChange={(e) => setSelected((s) => ({ ...s, [m.id]: e.target.value }))}
                      className="h-9 rounded-md border border-[var(--brand-border)] bg-white px-2 text-sm flex-1"
                      data-testid={`module-tier-select-${m.id}`}
                    >
                      {m.tiers.map((t) => {
                        const label = `${t.name} – ${t.month.toLocaleString("de-DE")} €/Monat`;
                        return (<option key={t.id} value={t.id}>{label}</option>);
                      })}
                    </select>
                    <Button size="sm" onClick={() => book(m.id)} disabled={busy === m.id}
                      className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid={`module-book-${m.id}`}>
                      {busy === m.id ? "…" : "Dazubuchen"}
                    </Button>
                  </div>
                  {selTier && (
                    <p className="text-[11px] text-[var(--brand-muted)] mt-2">oder {selTier.year.toLocaleString("de-DE")} €/Jahr · zzgl. MwSt.</p>
                  )}
                </>
              )}
              {isActive && !isOwner && (
                <p className="text-xs text-[var(--brand-muted)]">Verwaltung &amp; Kündigung über „Zahlungsdaten / Rechnungen" im Abo-Bereich.</p>
              )}
              {isActive && isOwner && (
                <p className="text-xs text-[var(--brand-muted)]">Als Plattform-Betreiber hast du automatisch vollen Zugriff (Premium).</p>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
};
